import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { AlertTriangle, Trash2, Check, Database, RefreshCw, Download, Clock, User, Mail, Calendar, Send, CheckCircle, XCircle, UserX, Power, PowerOff, Settings, ShieldCheck, KeyRound, Users, BookOpen, FileText, Loader2, UserPlus, Shield, Eye, EyeOff, Search, Crown, Star, ChevronUp, Megaphone } from 'lucide-react';
import { AnunciosAdmin } from '../components/admin/AnunciosAdmin';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// ==================== EXPORTAR TAB ====================
function ExportarTab() {
  const [loadingMatriculadosCSV, setLoadingMatriculadosCSV] = useState(false);
  const [loadingMatriculadosPDF, setLoadingMatriculadosPDF] = useState(false);
  const [loadingRecurseroCSV, setLoadingRecurseroCSV] = useState(false);
  const [loadingRecurseroPDF, setLoadingRecurseroPDF] = useState(false);

  const handleExportMatriculados = async (format) => {
    const setLoading = format === 'csv' ? setLoadingMatriculadosCSV : setLoadingMatriculadosPDF;
    setLoading(true);
    try {
      const endpoint = format === 'csv' 
        ? `${API_URL}/admin/export/matriculados` 
        : `${API_URL}/admin/export/matriculados/pdf`;
      
      const response = await axios.get(endpoint, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `matriculados.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Error exportando:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExportRecursero = async (format) => {
    const setLoading = format === 'csv' ? setLoadingRecurseroCSV : setLoadingRecurseroPDF;
    setLoading(true);
    try {
      const endpoint = format === 'csv' 
        ? `${API_URL}/admin/export/recursero` 
        : `${API_URL}/admin/export/recursero/pdf`;
      
      const response = await axios.get(endpoint, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `recursero.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Error exportando:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid md:grid-cols-2 gap-6">
      <Card>
        <CardHeader>
          <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mb-4">
            <Users className="w-6 h-6 text-primary" />
          </div>
          <CardTitle>Exportar Matriculados</CardTitle>
          <CardDescription>
            Descarga un archivo con todos los datos de los trabajadores sociales matriculados.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-gray-500 mb-4">
            <p><strong>Incluye:</strong></p>
            <ul className="list-disc list-inside mt-1 space-y-1">
              <li>Datos personales (nombre, DNI, matrícula)</li>
              <li>Información de contacto</li>
              <li>Domicilios real y laboral</li>
              <li>Título profesional</li>
            </ul>
          </div>
          <div className="space-y-2">
            <Button 
              onClick={() => handleExportMatriculados('csv')} 
              className="w-full" 
              data-testid="export-matriculados-csv-btn"
              disabled={loadingMatriculadosCSV}
            >
              {loadingMatriculadosCSV ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Exportar CSV
            </Button>
            <Button 
              onClick={() => handleExportMatriculados('pdf')} 
              variant="outline"
              className="w-full border-primary text-primary hover:bg-primary hover:text-white" 
              data-testid="export-matriculados-pdf-btn"
              disabled={loadingMatriculadosPDF}
            >
              {loadingMatriculadosPDF ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
              Exportar PDF
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
            <BookOpen className="w-6 h-6 text-emerald-600" />
          </div>
          <CardTitle>Exportar Recursero</CardTitle>
          <CardDescription>
            Descarga un archivo con los datos del recursero público (solo trabajadores visibles).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-gray-500 mb-4">
            <p><strong>Incluye:</strong></p>
            <ul className="list-disc list-inside mt-1 space-y-1">
              <li>Datos del profesional</li>
              <li>Lugares de trabajo</li>
              <li>Áreas de trabajo</li>
              <li>Información institucional</li>
            </ul>
          </div>
          <div className="space-y-2">
            <Button 
              onClick={() => handleExportRecursero('csv')} 
              className="w-full bg-emerald-500 hover:bg-emerald-600" 
              data-testid="export-recursero-csv-btn"
              disabled={loadingRecurseroCSV}
            >
              {loadingRecurseroCSV ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Exportar CSV
            </Button>
            <Button 
              onClick={() => handleExportRecursero('pdf')} 
              variant="outline"
              className="w-full border-emerald-500 text-emerald-600 hover:bg-emerald-500 hover:text-white" 
              data-testid="export-recursero-pdf-btn"
              disabled={loadingRecurseroPDF}
            >
              {loadingRecurseroPDF ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
              Exportar PDF
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== ADMINISTRADORES TAB ====================
function AdministradoresTab() {
  const { user: currentUser } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [newAdmin, setNewAdmin] = useState({ email: '', password: '', nombre: '', rol: 'administracion' });
  
  // Estado para cambio de rol
  const [cambioRolAdmin, setCambioRolAdmin] = useState(null);
  const [nuevoRol, setNuevoRol] = useState('');
  const [savingRol, setSavingRol] = useState(false);

  // Estado para promover usuario existente del portal
  const [promoverOpen, setPromoverOpen] = useState(false);
  const [promoverQuery, setPromoverQuery] = useState('');
  const [promoverBuscando, setPromoverBuscando] = useState(false);
  const [promoverResultados, setPromoverResultados] = useState([]);
  const [promoverSeleccionado, setPromoverSeleccionado] = useState(null);
  const [promoverRol, setPromoverRol] = useState('administracion');
  const [promoverGuardando, setPromoverGuardando] = useState(false);
  const [promoverError, setPromoverError] = useState('');

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/listar-admins`);
      setAdmins(response.data);
    } catch (err) {
      console.error('Error fetching admins:', err);
    } finally {
      setLoading(false);
    }
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!newAdmin.email || !newAdmin.password) {
      setError('El email y la contraseña son obligatorios');
      return;
    }

    if (!validateEmail(newAdmin.email)) {
      setError('El email ingresado no es válido');
      return;
    }

    if (newAdmin.password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setCreating(true);

    try {
      await axios.post(`${API_URL}/admin/crear-admin`, newAdmin);
      setSuccess(`${newAdmin.rol === 'admin' ? 'Administrador' : 'Usuario de Administración'} creado correctamente`);
      setNewAdmin({ email: '', password: '', nombre: '', rol: 'administracion' });
      fetchAdmins();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        const messages = detail.map(e => {
          if (e.loc?.includes('email')) return 'El email ingresado no es válido';
          if (e.loc?.includes('password')) return 'La contraseña no es válida';
          return e.msg || 'Error de validación';
        });
        setError(messages.join('. '));
      } else {
        setError(detail || 'Error al crear administrador');
      }
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteAdmin = async (adminId, email) => {
    if (!window.confirm(`¿Estás seguro de eliminar al administrador ${email}?`)) return;

    try {
      await axios.delete(`${API_URL}/admin/eliminar-admin/${adminId}`);
      fetchAdmins();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al eliminar administrador');
    }
  };
  
  const handleCambiarRol = async () => {
    if (!cambioRolAdmin || !nuevoRol) return;
    
    setSavingRol(true);
    try {
      await axios.put(`${API_URL}/admin/usuarios/${cambioRolAdmin.id}/rol`, { rol: nuevoRol });
      setCambioRolAdmin(null);
      setNuevoRol('');
      fetchAdmins();
      setSuccess(`Rol actualizado a ${nuevoRol}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al cambiar rol');
    } finally {
      setSavingRol(false);
    }
  };
  
  const getRolBadge = (rol) => {
    switch (rol) {
      case 'superadmin':
        return <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">Superadmin</span>;
      case 'admin':
        return <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">Admin</span>;
      case 'administracion':
        return <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">Administración</span>;
      default:
        return <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">{rol}</span>;
    }
  };

  // Buscar usuarios del portal para promover
  const buscarUsuariosPortal = async () => {
    setPromoverBuscando(true);
    setPromoverError('');
    try {
      const res = await axios.get(`${API_URL}/admin/usuarios-portal`, {
        params: { q: (promoverQuery || '').trim(), page_size: 100 },
      });
      const q = (promoverQuery || '').trim().toLowerCase();
      const emailsAdmin = new Set((admins || []).map(a => (a.email || '').toLowerCase()));
      const todos = res.data?.items || [];
      // Solo mostrar usuarios que HOY son trabajador_social (para no re-listar admins).
      // Filtrado por email/nombre/apellido/matricula si hay query.
      const filtrados = todos.filter(u => {
        if (u.rol !== 'trabajador_social') return false;
        if (emailsAdmin.has((u.email || '').toLowerCase())) return false;
        if (!q) return true;
        const t = u.trabajador_info || {};
        return (
          (u.email || '').toLowerCase().includes(q) ||
          (t.nombre || '').toLowerCase().includes(q) ||
          (t.apellido || '').toLowerCase().includes(q) ||
          String(t.numero_matricula || '').toLowerCase().includes(q) ||
          String(t.documento || '').toLowerCase().includes(q)
        );
      }).slice(0, 30);
      setPromoverResultados(filtrados);
    } catch (err) {
      setPromoverError(err.response?.data?.detail || 'Error buscando usuarios');
      setPromoverResultados([]);
    } finally {
      setPromoverBuscando(false);
    }
  };

  const handlePromoverUsuario = async () => {
    if (!promoverSeleccionado || !promoverRol) return;
    setPromoverGuardando(true);
    setPromoverError('');
    try {
      await axios.put(`${API_URL}/admin/usuarios/${promoverSeleccionado.id}/rol`, { rol: promoverRol });
      setSuccess(`${promoverSeleccionado.email} promovido a ${promoverRol}`);
      setPromoverOpen(false);
      setPromoverSeleccionado(null);
      setPromoverQuery('');
      setPromoverResultados([]);
      setPromoverRol('administracion');
      fetchAdmins();
    } catch (err) {
      setPromoverError(err.response?.data?.detail || 'Error al promover');
    } finally {
      setPromoverGuardando(false);
    }
  };

  // Separar admins por rol para renderizado agrupado
  const superadmins = admins.filter(a => a.rol === 'superadmin');
  const adminsRegulares = admins.filter(a => a.rol === 'admin');
  const administracionUsers = admins.filter(a => a.rol === 'administracion');
  const cuentaPrincipal = 'admin@colegio.com';

  const renderAdminCard = (admin) => (
    <div key={admin.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg" data-testid={`admin-row-${admin.email}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-medium text-gray-900 truncate">{admin.nombre || admin.email}</p>
          {getRolBadge(admin.rol)}
          {admin.email === cuentaPrincipal && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 flex items-center gap-1" data-testid="badge-cuenta-principal">
              <Star className="w-3 h-3" /> Cuenta principal
            </span>
          )}
          {admin.email === currentUser?.email && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-slate-200 text-slate-700">Vos</span>
          )}
        </div>
        <p className="text-sm text-gray-500 truncate">{admin.email}</p>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        {(currentUser?.rol === 'superadmin' || currentUser?.rol === 'admin') && admin.email !== currentUser?.email && (
          <Button
            variant="ghost"
            size="sm"
            className="text-purple-500 hover:text-purple-700 hover:bg-purple-50"
            onClick={() => {
              setCambioRolAdmin(admin);
              setNuevoRol(admin.rol);
            }}
            title="Cambiar rol"
            data-testid={`change-role-${admin.email}`}
          >
            <Shield className="w-4 h-4" />
          </Button>
        )}
        {(currentUser?.rol === 'superadmin' || currentUser?.rol === 'admin') && admin.email !== cuentaPrincipal && admin.email !== currentUser?.email && (
          <Button
            variant="ghost"
            size="sm"
            className="text-red-500 hover:text-red-700 hover:bg-red-50"
            onClick={() => handleDeleteAdmin(admin.id, admin.email)}
            data-testid={`delete-admin-${admin.email}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5" />
            Crear Nuevo Administrador
          </CardTitle>
          <CardDescription>
            Los administradores tienen acceso completo al panel de gestión.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleCreateAdmin} className="space-y-4">
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                <AlertTriangle className="w-4 h-4" />
                {error}
              </div>
            )}
            {success && (
              <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-700 text-sm">
                <Check className="w-4 h-4" />
                {success}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="nombre">Nombre (opcional)</Label>
              <Input
                id="nombre"
                placeholder="Nombre del administrador"
                value={newAdmin.nombre}
                onChange={(e) => setNewAdmin(prev => ({ ...prev, nombre: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                placeholder="admin@ejemplo.com"
                value={newAdmin.email}
                onChange={(e) => setNewAdmin(prev => ({ ...prev, email: e.target.value }))}
                required
                data-testid="admin-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Contraseña *</Label>
              <Input
                id="password"
                type="password"
                placeholder="Mínimo 6 caracteres"
                value={newAdmin.password}
                onChange={(e) => setNewAdmin(prev => ({ ...prev, password: e.target.value }))}
                required
                minLength={6}
                data-testid="admin-password-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="rol">Tipo de Usuario *</Label>
              <select
                id="rol"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={newAdmin.rol}
                onChange={(e) => setNewAdmin(prev => ({ ...prev, rol: e.target.value }))}
                data-testid="admin-rol-select"
              >
                <option value="administracion">Administración (acceso limitado)</option>
                <option value="admin">Superadministrador (acceso total)</option>
              </select>
              <p className="text-xs text-gray-500">
                {newAdmin.rol === 'administracion' 
                  ? '• Puede ver y editar contenido, pero no puede borrar datos ni exportar/importar configuraciones'
                  : '• Acceso completo: puede borrar datos, exportar/importar, y realizar todas las acciones'}
              </p>
            </div>

            <Button type="submit" className="w-full" disabled={creating} data-testid="create-admin-btn">
              <UserPlus className="w-4 h-4 mr-2" />
              {creating ? 'Creando...' : `Crear ${newAdmin.rol === 'admin' ? 'Superadministrador' : 'Usuario de Administración'}`}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5" />
                Administradores Actuales
              </CardTitle>
              <CardDescription>
                {admins.length} cuenta{admins.length !== 1 ? 's' : ''} con permisos administrativos
                {superadmins.length > 0 && ` · ${superadmins.length} superadmin${superadmins.length !== 1 ? 's' : ''}`}
              </CardDescription>
            </div>
            {(currentUser?.rol === 'superadmin' || currentUser?.rol === 'admin') && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPromoverOpen(true)}
                data-testid="btn-promover-usuario"
                className="flex items-center gap-1.5"
              >
                <ChevronUp className="w-4 h-4" />
                Promover usuario existente
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            </div>
          ) : admins.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No hay administradores</p>
          ) : (
            <div className="space-y-5">
              {/* Superadmins */}
              <div data-testid="section-superadmins">
                <div className="flex items-center gap-2 mb-2">
                  <Crown className="w-4 h-4 text-purple-600" />
                  <h4 className="text-sm font-semibold text-purple-700">
                    Superadministradores ({superadmins.length})
                  </h4>
                </div>
                {superadmins.length === 0 ? (
                  <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg text-sm text-purple-700">
                    No hay cuentas con rol Superadmin. Podés promover una cuenta existente desde el botón &quot;Promover usuario existente&quot; o cambiar el rol de un admin.
                  </div>
                ) : (
                  <div className="space-y-2">{superadmins.map(renderAdminCard)}</div>
                )}
              </div>

              {/* Admins */}
              <div data-testid="section-admins">
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="w-4 h-4 text-blue-600" />
                  <h4 className="text-sm font-semibold text-blue-700">
                    Admins ({adminsRegulares.length})
                  </h4>
                </div>
                {adminsRegulares.length === 0 ? (
                  <p className="text-sm text-gray-500 italic px-1">Sin cuentas con rol Admin.</p>
                ) : (
                  <div className="space-y-2">{adminsRegulares.map(renderAdminCard)}</div>
                )}
              </div>

              {/* Administracion */}
              <div data-testid="section-administracion">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-4 h-4 text-green-600" />
                  <h4 className="text-sm font-semibold text-green-700">
                    Administración ({administracionUsers.length})
                  </h4>
                </div>
                {administracionUsers.length === 0 ? (
                  <p className="text-sm text-gray-500 italic px-1">Sin cuentas con rol Administración.</p>
                ) : (
                  <div className="space-y-2">{administracionUsers.map(renderAdminCard)}</div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Modal Cambiar Rol */}
      <Dialog open={!!cambioRolAdmin} onOpenChange={() => setCambioRolAdmin(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-purple-600" />
              Cambiar Rol de Administrador
            </DialogTitle>
          </DialogHeader>
          
          {cambioRolAdmin && (
            <div className="space-y-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="font-medium">{cambioRolAdmin.nombre || cambioRolAdmin.email}</p>
                <p className="text-sm text-gray-600">{cambioRolAdmin.email}</p>
                <p className="text-xs text-gray-500 mt-1">
                  Rol actual: {getRolBadge(cambioRolAdmin.rol)}
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Nuevo rol</label>
                <select
                  value={nuevoRol}
                  onChange={(e) => setNuevoRol(e.target.value)}
                  className="w-full p-2 border rounded-lg"
                >
                  <option value="administracion">Administración</option>
                  <option value="admin">Admin</option>
                  <option value="superadmin">Superadmin</option>
                  <option value="trabajador_social">Usuario (quitar permisos de admin)</option>
                </select>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCambioRolAdmin(null)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleCambiarRol}
              disabled={savingRol || nuevoRol === cambioRolAdmin?.rol}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {savingRol ? 'Guardando...' : 'Cambiar Rol'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Promover Usuario Existente */}
      <Dialog open={promoverOpen} onOpenChange={(open) => {
        setPromoverOpen(open);
        if (!open) {
          setPromoverSeleccionado(null);
          setPromoverResultados([]);
          setPromoverQuery('');
          setPromoverError('');
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ChevronUp className="w-5 h-5 text-purple-600" />
              Promover usuario existente
            </DialogTitle>
            <CardDescription>
              Buscá una cuenta del portal por email, nombre, apellido, matrícula o DNI y asignale un rol administrativo.
            </CardDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex gap-2">
              <Input
                placeholder="Email, nombre, matrícula o DNI…"
                value={promoverQuery}
                onChange={e => setPromoverQuery(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); buscarUsuariosPortal(); } }}
                data-testid="input-buscar-usuario-promover"
                className="flex-1"
              />
              <Button
                variant="outline"
                onClick={buscarUsuariosPortal}
                disabled={promoverBuscando}
                data-testid="btn-buscar-usuario-promover"
              >
                {promoverBuscando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              </Button>
            </div>

            {promoverError && (
              <div className="p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">{promoverError}</div>
            )}

            {promoverResultados.length > 0 && !promoverSeleccionado && (
              <div className="max-h-64 overflow-y-auto border rounded-lg divide-y" data-testid="lista-resultados-promover">
                {promoverResultados.map(u => {
                  const t = u.trabajador_info || {};
                  return (
                    <button
                      type="button"
                      key={u.id}
                      className="w-full text-left p-3 hover:bg-purple-50 transition-colors"
                      onClick={() => setPromoverSeleccionado(u)}
                      data-testid={`resultado-usuario-${u.email}`}
                    >
                      <p className="font-medium text-gray-900">
                        {(t.apellido || '') + ' ' + (t.nombre || '')} {t.numero_matricula && <span className="text-xs text-gray-500">· Mat. {t.numero_matricula}</span>}
                      </p>
                      <p className="text-sm text-gray-500">{u.email}</p>
                    </button>
                  );
                })}
              </div>
            )}

            {promoverResultados.length === 0 && !promoverBuscando && promoverQuery && !promoverSeleccionado && (
              <p className="text-sm text-gray-500 italic">No se encontraron usuarios que coincidan.</p>
            )}

            {promoverSeleccionado && (
              <div className="space-y-3">
                <div className="p-3 bg-gray-50 rounded-lg flex items-center justify-between">
                  <div>
                    <p className="font-medium">
                      {(promoverSeleccionado.trabajador_info?.apellido || '') + ' ' + (promoverSeleccionado.trabajador_info?.nombre || '')}
                    </p>
                    <p className="text-sm text-gray-600">{promoverSeleccionado.email}</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setPromoverSeleccionado(null)}>
                    Cambiar
                  </Button>
                </div>
                <div>
                  <Label>Rol a asignar</Label>
                  <select
                    value={promoverRol}
                    onChange={e => setPromoverRol(e.target.value)}
                    className="w-full mt-1 p-2 border rounded-lg"
                    data-testid="select-rol-promover"
                  >
                    <option value="administracion">Administración</option>
                    <option value="admin">Admin</option>
                    <option value="superadmin">Superadmin</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Superadmin: acceso completo, puede crear/eliminar otros admins.<br />
                    Admin: acceso al panel de gestión, no puede tocar otros admins.<br />
                    Administración: acceso limitado (sin acciones destructivas).
                  </p>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPromoverOpen(false)}>Cancelar</Button>
            <Button
              onClick={handlePromoverUsuario}
              disabled={!promoverSeleccionado || promoverGuardando}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="btn-confirmar-promocion"
            >
              {promoverGuardando ? 'Promoviendo…' : `Promover a ${promoverRol}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==================== RESET CONTRASEÑAS TAB ====================
function ResetPasswordsTab() {
  const [solicitudes, setSolicitudes] = useState([]);
  const [matriculados, setMatriculados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [emailBuscar, setEmailBuscar] = useState('');
  const [usuarioEncontrado, setUsuarioEncontrado] = useState(null);
  const [nuevaPassword, setNuevaPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [reseteando, setReseteando] = useState(false);
  const [buscando, setBuscando] = useState(false);
  const [filtroMatriculado, setFiltroMatriculado] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [solicitudesRes, matriculadosRes] = await Promise.all([
        axios.get(`${API_URL}/admin/solicitudes-reset`),
        axios.get(`${API_URL}/admin/usuarios-con-cuenta`)
      ]);
      setSolicitudes(solicitudesRes.data);
      setMatriculados(matriculadosRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const buscarUsuario = async (e) => {
    e?.preventDefault();
    setError('');
    setUsuarioEncontrado(null);
    setBuscando(true);

    try {
      const response = await axios.get(`${API_URL}/admin/buscar-usuario?email=${encodeURIComponent(emailBuscar)}`);
      setUsuarioEncontrado(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Usuario no encontrado');
    } finally {
      setBuscando(false);
    }
  };

  const seleccionarMatriculado = (mat) => {
    setEmailBuscar(mat.email);
    setUsuarioEncontrado({
      id: mat.user_id,
      email: mat.email,
      rol: 'trabajador_social',
      trabajador_nombre: `${mat.nombre} ${mat.apellido}`
    });
    setError('');
    setSuccess('');
    setNuevaPassword('');
  };

  const resetearPassword = async () => {
    if (!usuarioEncontrado || !nuevaPassword) return;
    
    if (nuevaPassword.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setReseteando(true);
    setError('');
    setSuccess('');

    try {
      await axios.post(`${API_URL}/admin/reset-password`, {
        user_id: usuarioEncontrado.id,
        nueva_password: nuevaPassword
      });
      setSuccess(`Contraseña reseteada correctamente para ${usuarioEncontrado.email}`);
      setUsuarioEncontrado(null);
      setEmailBuscar('');
      setNuevaPassword('');
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al resetear contraseña');
    } finally {
      setReseteando(false);
    }
  };

  const marcarAtendida = async (solicitudId) => {
    try {
      await axios.put(`${API_URL}/admin/solicitudes-reset/${solicitudId}/atender`);
      fetchData();
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const formatFecha = (fecha) => {
    if (!fecha) return 'N/A';
    const date = new Date(fecha);
    return date.toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const matriculadosFiltrados = matriculados.filter(mat => {
    if (!filtroMatriculado) return true;
    const busqueda = filtroMatriculado.toLowerCase();
    return (
      mat.nombre?.toLowerCase().includes(busqueda) ||
      mat.apellido?.toLowerCase().includes(busqueda) ||
      mat.email?.toLowerCase().includes(busqueda) ||
      mat.numero_matricula?.toLowerCase().includes(busqueda)
    );
  });

  return (
    <div>
      {error && (
        <div className="mb-6 flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          <AlertTriangle className="w-4 h-4" />
          {error}
        </div>
      )}
      {success && (
        <div className="mb-6 flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-700 text-sm">
          <Check className="w-4 h-4" />
          {success}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <Card className="xl:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Users className="w-5 h-5" />
              Matriculadxs con Cuenta
              <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                {matriculados.length}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="mb-4">
              <Input
                type="text"
                placeholder="Buscar por nombre, email o matrícula..."
                value={filtroMatriculado}
                onChange={(e) => setFiltroMatriculado(e.target.value)}
                className="text-sm"
              />
            </div>
            
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
              </div>
            ) : matriculadosFiltrados.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <User className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No se encontraron matriculados</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {matriculadosFiltrados.map((mat) => (
                  <button
                    key={mat.user_id}
                    onClick={() => seleccionarMatriculado(mat)}
                    className={`w-full text-left p-3 rounded-lg border transition-colors ${
                      usuarioEncontrado?.id === mat.user_id
                        ? 'bg-blue-50 border-blue-300'
                        : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    <p className="font-medium text-gray-900 text-sm truncate">
                      {mat.apellido}, {mat.nombre}
                    </p>
                    <p className="text-xs text-gray-500 truncate">{mat.email}</p>
                    <p className="text-xs text-gray-400">Mat. {mat.numero_matricula}</p>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="xl:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <KeyRound className="w-5 h-5" />
              Resetear Contraseña
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <form onSubmit={buscarUsuario} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email-buscar">Buscar por email</Label>
                <div className="flex gap-2">
                  <Input
                    id="email-buscar"
                    type="email"
                    placeholder="email@ejemplo.com"
                    value={emailBuscar}
                    onChange={(e) => setEmailBuscar(e.target.value)}
                    required
                  />
                  <Button type="submit" disabled={buscando} size="sm">
                    <Search className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </form>

            {usuarioEncontrado && (
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-4">
                <div>
                  <p className="text-sm text-blue-700 font-medium">Usuario seleccionado:</p>
                  <p className="text-blue-900 font-semibold">{usuarioEncontrado.email}</p>
                  {usuarioEncontrado.trabajador_nombre && (
                    <p className="text-sm text-blue-600">{usuarioEncontrado.trabajador_nombre}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="nueva-password">Nueva contraseña</Label>
                  <div className="relative">
                    <Input
                      id="nueva-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Mínimo 6 caracteres"
                      value={nuevaPassword}
                      onChange={(e) => setNuevaPassword(e.target.value)}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <Button 
                  onClick={resetearPassword} 
                  disabled={reseteando || !nuevaPassword}
                  className="w-full"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {reseteando ? 'Reseteando...' : 'Resetear Contraseña'}
                </Button>
              </div>
            )}

            {!usuarioEncontrado && (
              <div className="text-center py-8 text-gray-400">
                <KeyRound className="w-12 h-12 mx-auto mb-3 text-gray-200" />
                <p className="text-sm">Seleccioná un matriculado de la lista o buscá por email</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="xl:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Clock className="w-5 h-5" />
              Solicitudes Pendientes
              {solicitudes.length > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">
                  {solicitudes.length}
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
              </div>
            ) : solicitudes.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Mail className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No hay solicitudes pendientes</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {solicitudes.map((sol) => (
                  <div key={sol.id} className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                    <div className="mb-2">
                      <p className="font-medium text-gray-900 text-sm">{sol.email}</p>
                      <p className="text-xs text-gray-500">{formatFecha(sol.fecha_solicitud)}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="flex-1 text-xs"
                        onClick={() => {
                          setEmailBuscar(sol.email);
                          buscarUsuario();
                        }}
                      >
                        <Search className="w-3 h-3 mr-1" />
                        Buscar
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 text-xs"
                        onClick={() => marcarAtendida(sol.id)}
                      >
                        <Check className="w-3 h-3 mr-1" />
                        Atendida
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ==================== SISTEMA TAB (Original ConfiguracionContent) ====================
function SistemaTab({ isSuperAdmin }) {
  const [deleteRecurseroText, setDeleteRecurseroText] = useState('');
  const [deleteAllText, setDeleteAllText] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  const [depurarText, setDepurarText] = useState('');
  const [depurando, setDepurando] = useState(false);
  const [depurarPreview, setDepurarPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  
  const [backups, setBackups] = useState([]);
  const [loadingBackups, setLoadingBackups] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState(null);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const [restoreText, setRestoreText] = useState('');
  
  const [recordatoriosEstado, setRecordatoriosEstado] = useState(null);
  const [recordatoriosLog, setRecordatoriosLog] = useState([]);
  const [loadingRecordatorios, setLoadingRecordatorios] = useState(false);
  const [enviandoRecordatorios, setEnviandoRecordatorios] = useState(false);
  const [showEnvioResult, setShowEnvioResult] = useState(false);
  const [envioResult, setEnvioResult] = useState(null);
  
  const [configEnvios, setConfigEnvios] = useState([]);
  const [killSwitchActivo, setKillSwitchActivo] = useState(false);
  const [killSwitchInfo, setKillSwitchInfo] = useState(null);
  const [togglingKillSwitch, setTogglingKillSwitch] = useState(false);
  const [loadingConfigEnvios, setLoadingConfigEnvios] = useState(false);
  const [togglingEnvio, setTogglingEnvio] = useState(null);
  
  const [cuentaEmail, setCuentaEmail] = useState(null);
  const [loadingCuentaEmail, setLoadingCuentaEmail] = useState(false);
  const [cambiandoCuenta, setCambiandoCuenta] = useState(false);

  useEffect(() => {
    fetchBackups();
    fetchRecordatoriosEstado();
    fetchRecordatoriosLog();
    fetchConfigEnvios();
    fetchCuentaEmail();
  }, []);

  const fetchCuentaEmail = async () => {
    setLoadingCuentaEmail(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/antuar/config-cuenta-email`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCuentaEmail(res.data);
    } catch (err) {
      console.error('Error cargando cuenta de email:', err);
    } finally {
      setLoadingCuentaEmail(false);
    }
  };

  const handleCambiarCuenta = async (cuenta) => {
    setCambiandoCuenta(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/antuar/config-cuenta-email`, 
        { cuenta },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      fetchCuentaEmail();
      setSuccessMessage(`Cuenta de email cambiada correctamente`);
      setShowSuccess(true);
    } catch (err) {
      console.error('Error cambiando cuenta:', err);
      alert('Error al cambiar la cuenta de email');
    } finally {
      setCambiandoCuenta(false);
    }
  };

  const fetchConfigEnvios = async () => {
    setLoadingConfigEnvios(true);
    try {
      const token = localStorage.getItem('token');
      const [envios, killSwitch] = await Promise.all([
        axios.get(`${API_URL}/antuar/config-envios`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/antuar/kill-switch-envios`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { activo: false } })),
      ]);
      setConfigEnvios(envios.data);
      setKillSwitchActivo(!!killSwitch.data?.activo);
      setKillSwitchInfo(killSwitch.data);
    } catch (err) {
      console.error('Error cargando config de envíos:', err);
    } finally {
      setLoadingConfigEnvios(false);
    }
  };

  const handleToggleKillSwitch = async () => {
    const activar = !killSwitchActivo;
    const mensaje = activar
      ? '⚠️ ATENCIÓN\n\nEsto va a SUSPENDER TODOS los envíos de email del sistema (estado de cuenta, capacitaciones, campus, certificados, todos).\n\nLas colas que estén corriendo se detendrán en caliente.\n\n¿Estás seguro?'
      : '¿Reanudar los envíos de email? Cada tipo volverá a su configuración individual.';
    if (!window.confirm(mensaje)) return;
    let motivo = null;
    if (activar) {
      motivo = window.prompt('Motivo (opcional):', '') || null;
    }
    setTogglingKillSwitch(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/antuar/kill-switch-envios`,
        { activo: activar, motivo },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setKillSwitchActivo(!!res.data?.activo);
      setSuccessMessage(res.data?.mensaje || (activar ? 'Kill switch activado' : 'Kill switch desactivado'));
      setShowSuccess(true);
      await fetchConfigEnvios();
    } catch (err) {
      console.error('Error toggling kill switch:', err);
      alert('Error al cambiar el estado del kill switch');
    } finally {
      setTogglingKillSwitch(false);
    }
  };

  const handleToggleEnvio = async (tipoEmail, estadoActual) => {
    const nuevoEstado = !estadoActual;
    const accion = nuevoEstado ? 'ACTIVAR' : 'SUSPENDER';
    
    if (!window.confirm(`¿Estás seguro de ${accion} los envíos de este tipo de email?`)) return;
    
    setTogglingEnvio(tipoEmail);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/antuar/config-envios/${tipoEmail}`, 
        { activo: nuevoEstado },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      fetchConfigEnvios();
      setSuccessMessage(`Envíos ${nuevoEstado ? 'activados' : 'suspendidos'} correctamente`);
      setShowSuccess(true);
    } catch (err) {
      console.error('Error toggling envío:', err);
      alert('Error al cambiar el estado del envío');
    } finally {
      setTogglingEnvio(null);
    }
  };

  const handleMarcarEnviadosMes = async () => {
    if (!window.confirm('¿Estás seguro? Esto marcará TODOS los matriculados como que ya recibieron el recordatorio este mes.')) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_URL}/antuar/marcar-enviados-mes-actual`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccessMessage(res.data.mensaje);
      setShowSuccess(true);
    } catch (err) {
      console.error('Error marcando enviados:', err);
      alert('Error al marcar emails como enviados');
    } finally {
      setLoading(false);
    }
  };

  const fetchBackups = async () => {
    setLoadingBackups(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/admin/backups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBackups(res.data);
    } catch (err) {
      console.error('Error cargando backups:', err);
    } finally {
      setLoadingBackups(false);
    }
  };

  const fetchRecordatoriosEstado = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/antuar/recordatorios-estado`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecordatoriosEstado(res.data);
    } catch (err) {
      console.error('Error cargando estado de recordatorios:', err);
    }
  };

  const fetchRecordatoriosLog = async () => {
    setLoadingRecordatorios(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/antuar/recordatorios-log?limit=5`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecordatoriosLog(res.data);
    } catch (err) {
      console.error('Error cargando log de recordatorios:', err);
    } finally {
      setLoadingRecordatorios(false);
    }
  };

  const handleEnviarRecordatorios = async () => {
    if (!window.confirm('¿Estás seguro de enviar el recordatorio a TODOS los matriculados con email?')) return;
    
    setEnviandoRecordatorios(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_URL}/antuar/enviar-recordatorio-mensual`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEnvioResult(res.data);
      setShowEnvioResult(true);
      fetchRecordatoriosEstado();
      fetchRecordatoriosLog();
    } catch (err) {
      console.error('Error enviando recordatorios:', err);
      setEnvioResult({
        success: false,
        mensaje: err.response?.data?.detail || 'Error al enviar los recordatorios'
      });
      setShowEnvioResult(true);
    } finally {
      setEnviandoRecordatorios(false);
    }
  };

  const fetchDepurarPreview = async () => {
    setLoadingPreview(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/trabajadores/admin/depurar-preview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDepurarPreview(res.data);
    } catch (err) {
      console.error('Error obteniendo preview:', err);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleDepurarMatriculados = async () => {
    if (depurarText !== 'DEPURAR') return;
    
    setDepurando(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_URL}/trabajadores/admin/depurar`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccessMessage(`Depuración completada: ${res.data.eliminados} matriculados sin usuario eliminados`);
      setShowSuccess(true);
      setDepurarText('');
      setDepurarPreview(null);
    } catch (err) {
      console.error('Error depurando:', err);
      alert('Error al depurar matriculados');
    } finally {
      setDepurando(false);
    }
  };

  const handleCreateBackup = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/admin/backups/crear`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccessMessage('Backup creado exitosamente');
      setShowSuccess(true);
      fetchBackups();
    } catch (err) {
      console.error('Error creando backup:', err);
      alert('Error al crear backup');
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreBackup = async () => {
    if (restoreText !== 'RESTAURAR' || !selectedBackup) return;
    
    setLoading(true);
    try {
      await axios.post(`${API_URL}/admin/backups/${selectedBackup.id}/restaurar`);
      setSuccessMessage(`Backup del ${formatDate(selectedBackup.fecha)} restaurado exitosamente`);
      setShowSuccess(true);
      setShowRestoreConfirm(false);
      setRestoreText('');
      setSelectedBackup(null);
    } catch (err) {
      console.error('Error restaurando backup:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBackup = async (backupId) => {
    if (!window.confirm('¿Estás seguro de eliminar este backup?')) return;
    
    try {
      await axios.delete(`${API_URL}/admin/backups/${backupId}`);
      fetchBackups();
    } catch (err) {
      console.error('Error eliminando backup:', err);
    }
  };

  const handleDeleteRecursero = async () => {
    if (deleteRecurseroText !== 'BORRAR') return;
    
    setLoading(true);
    try {
      await axios.delete(`${API_URL}/admin/delete/recursero`);
      setDeleteRecurseroText('');
      setSuccessMessage('Datos del recursero eliminados correctamente');
      setShowSuccess(true);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAll = async () => {
    if (deleteAllText !== 'BORRAR TODO') return;
    
    setLoading(true);
    try {
      const res = await axios.delete(`${API_URL}/admin/delete/matriculados`);
      setDeleteAllText('');
      const backupInfo = res.data.backup_creado;
      setSuccessMessage(
        `Base de datos eliminada. Se creó un backup automático (${backupInfo?.estadisticas?.trabajadores || 0} trabajadores, ${backupInfo?.estadisticas?.instituciones || 0} instituciones)`
      );
      setShowSuccess(true);
      fetchBackups();
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (isoDate) => {
    const date = new Date(isoDate);
    return date.toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getMotivoLabel = (motivo) => {
    if (!motivo) return 'Manual';
    if (motivo === 'pre-borrar-todo') return '🔴 Pre-Borrar Todo';
    if (motivo === 'manual') return '📦 Manual';
    if (motivo.startsWith && motivo.startsWith('pre-restauracion')) return '🔄 Pre-Restauración';
    return motivo;
  };

  return (
    <div className="space-y-6">
      {/* Cuenta de Email */}
      <Card className="border-blue-200">
        <CardHeader>
          <CardTitle className="text-blue-700 flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Cuenta de Email para Envíos
          </CardTitle>
          <CardDescription>
            Elegí desde qué cuenta se envían los emails.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadingCuentaEmail ? (
            <div className="text-center py-4">
              <RefreshCw className="w-5 h-5 animate-spin mx-auto text-gray-400" />
            </div>
          ) : cuentaEmail ? (
            <div className="space-y-3">
              {cuentaEmail.cuentas_disponibles?.map((cuenta) => (
                <div 
                  key={cuenta.id}
                  className={`p-4 rounded-lg border-2 transition-colors cursor-pointer ${
                    cuentaEmail.cuenta_activa === cuenta.id
                      ? 'bg-blue-50 border-blue-400'
                      : 'bg-gray-50 border-gray-200 hover:border-blue-300'
                  }`}
                  onClick={() => cuentaEmail.cuenta_activa !== cuenta.id && handleCambiarCuenta(cuenta.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        cuentaEmail.cuenta_activa === cuenta.id
                          ? 'border-blue-500 bg-blue-500'
                          : 'border-gray-400'
                      }`}>
                        {cuentaEmail.cuenta_activa === cuenta.id && (
                          <Check className="w-3 h-3 text-white" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{cuenta.email}</p>
                        <p className="text-sm text-gray-500">{cuenta.descripcion}</p>
                      </div>
                    </div>
                    {cuentaEmail.cuenta_activa === cuenta.id && (
                      <span className="text-xs bg-blue-200 text-blue-800 px-2 py-1 rounded-full">ACTIVA</span>
                    )}
                  </div>
                </div>
              ))}
              {cambiandoCuenta && (
                <p className="text-sm text-gray-500 text-center">
                  <RefreshCw className="w-4 h-4 inline animate-spin mr-2" />
                  Cambiando cuenta...
                </p>
              )}
            </div>
          ) : (
            <p className="text-gray-500">No se pudo cargar la configuración</p>
          )}
        </CardContent>
      </Card>

      {/* Control de Envíos */}
      <Card className="border-violet-200">
        <CardHeader>
          <CardTitle className="text-violet-700 flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Control de Envíos Automáticos
          </CardTitle>
          <CardDescription>
            Activá o suspendé los envíos automáticos de email por tipo.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* ==== KILL SWITCH MASTER ==== */}
          <div
            className={`mb-6 p-5 rounded-2xl border-2 ${
              killSwitchActivo
                ? 'bg-red-50 border-red-400 shadow-md'
                : 'bg-white border-gray-200'
            }`}
            data-testid="kill-switch-container"
          >
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${
                  killSwitchActivo ? 'bg-red-600 animate-pulse' : 'bg-gray-100'
                }`}>
                  <PowerOff className={`w-6 h-6 ${killSwitchActivo ? 'text-white' : 'text-gray-500'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`font-bold text-base ${killSwitchActivo ? 'text-red-900' : 'text-gray-900'}`}>
                    Botón de emergencia · Suspender TODOS los envíos
                  </p>
                  <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                    {killSwitchActivo
                      ? 'Todos los envíos de email del sistema están detenidos. Ningún email saldrá hasta que se desactive.'
                      : 'Corta todos los envíos de email (estado de cuenta, capacitaciones, campus, certificados, etc.). Úsalo solo en emergencias.'}
                  </p>
                  {killSwitchActivo && killSwitchInfo?.motivo && (
                    <p className="text-xs text-red-800 mt-2 italic">Motivo: {killSwitchInfo.motivo}</p>
                  )}
                  {killSwitchActivo && killSwitchInfo?.ultima_modificacion && (
                    <p className="text-[11px] text-red-700 mt-1">
                      Activado por {killSwitchInfo.modificado_por || 'admin'} · {new Date(killSwitchInfo.ultima_modificacion).toLocaleString('es-AR')}
                    </p>
                  )}
                </div>
              </div>
              <Button
                onClick={handleToggleKillSwitch}
                disabled={togglingKillSwitch}
                size="lg"
                className={
                  killSwitchActivo
                    ? 'bg-emerald-600 hover:bg-emerald-700 text-white font-semibold shadow-md rounded-xl'
                    : 'bg-red-600 hover:bg-red-700 text-white font-semibold shadow-md rounded-xl'
                }
                data-testid="kill-switch-toggle"
              >
                {togglingKillSwitch ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : killSwitchActivo ? (
                  <>
                    <Power className="w-4 h-4 mr-2" />
                    Reanudar envíos
                  </>
                ) : (
                  <>
                    <PowerOff className="w-4 h-4 mr-2" />
                    🛑 SUSPENDER TODO
                  </>
                )}
              </Button>
            </div>
          </div>

          {killSwitchActivo && (
            <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-900" data-testid="kill-switch-banner">
              <strong>Aviso:</strong> mientras el kill switch esté activo, los toggles individuales de abajo se ignoran. Ningún email se enviará.
            </div>
          )}

          {loadingConfigEnvios ? (
            <div className="text-center py-4">
              <RefreshCw className="w-5 h-5 animate-spin mx-auto text-gray-400" />
            </div>
          ) : (
            <div className="space-y-3">
              {configEnvios.map((config) => (
                <div 
                  key={config.id} 
                  className={`p-4 rounded-lg border-2 transition-colors ${
                    config.activo ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        {config.activo ? (
                          <Power className="w-5 h-5 text-green-600" />
                        ) : (
                          <PowerOff className="w-5 h-5 text-red-600" />
                        )}
                        <span className="font-medium text-gray-900">{config.nombre}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          config.activo ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'
                        }`}>
                          {config.activo ? 'ACTIVO' : 'SUSPENDIDO'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{config.descripcion}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        <Clock className="w-3 h-3 inline mr-1" />
                        Horario: {config.horario}
                      </p>
                    </div>
                    <Button
                      onClick={() => handleToggleEnvio(config.id, config.activo)}
                      disabled={togglingEnvio === config.id}
                      variant={config.activo ? "destructive" : "default"}
                      className={!config.activo ? "bg-green-600 hover:bg-green-700" : ""}
                    >
                      {togglingEnvio === config.id ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : config.activo ? (
                        <>
                          <PowerOff className="w-4 h-4 mr-2" />
                          Suspender
                        </>
                      ) : (
                        <>
                          <Power className="w-4 h-4 mr-2" />
                          Activar
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ))}
              
              {configEnvios.length === 0 && (
                <p className="text-gray-500 text-center py-4">No hay tipos de envío configurados</p>
              )}
              
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-700">Marcar recordatorios como enviados</p>
                    <p className="text-xs text-gray-500">Usar si ya se enviaron los recordatorios este mes</p>
                  </div>
                  <Button
                    onClick={handleMarcarEnviadosMes}
                    disabled={loading}
                    variant="outline"
                    className="border-amber-300 text-amber-700 hover:bg-amber-50"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Marcar todos como enviados
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Backups */}
      <Card className="border-blue-200">
        <CardHeader>
          <CardTitle className="text-blue-700 flex items-center gap-2">
            <Database className="w-5 h-5" />
            Backups de Seguridad
          </CardTitle>
          <CardDescription>
            Los backups se crean automáticamente antes de operaciones destructivas.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Button onClick={handleCreateBackup} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
                <Download className="w-4 h-4 mr-2" />
                Crear Backup Manual
              </Button>
              <Button variant="outline" onClick={fetchBackups} disabled={loadingBackups}>
                <RefreshCw className={`w-4 h-4 ${loadingBackups ? 'animate-spin' : ''}`} />
              </Button>
            </div>

            {backups.length > 0 ? (
              <div className="border rounded-lg divide-y max-h-64 overflow-y-auto">
                {backups.map((backup) => (
                  <div key={backup.id} className="p-3 hover:bg-gray-50 flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <Clock className="w-4 h-4 text-gray-400" />
                        {formatDate(backup.fecha)}
                        {backup.motivo && (
                          <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">
                            {getMotivoLabel(backup.motivo)}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 mt-1 flex items-center gap-3">
                        <span>{backup.stats?.trabajadores || backup.estadisticas?.trabajadores || 0} trabajadores</span>
                        <span>{backup.stats?.instituciones || backup.estadisticas?.instituciones || 0} instituciones</span>
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {backup.creado_por || 'sistema'}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-blue-600 hover:bg-blue-50"
                        onClick={() => {
                          setSelectedBackup(backup);
                          setShowRestoreConfirm(true);
                        }}
                      >
                        <RefreshCw className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-500 hover:bg-red-50"
                        onClick={() => handleDeleteBackup(backup.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">
                No hay backups disponibles.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Recordatorios */}
      <Card className="border-purple-200">
        <CardHeader>
          <CardTitle className="text-purple-700 flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Recordatorios de Vencimiento Mensual
          </CardTitle>
          <CardDescription>
            Sistema de envío automático de recordatorios de pago de matrícula.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {recordatoriosEstado && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-purple-700 font-medium mb-1">
                    <Calendar className="w-4 h-4" />
                    Próximo envío automático
                  </div>
                  <p className="text-lg font-semibold text-purple-900">
                    {recordatoriosEstado.proximo_envio_programado}
                  </p>
                </div>
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-blue-700 font-medium mb-1">
                    <Clock className="w-4 h-4" />
                    Fecha de vencimiento actual
                  </div>
                  <p className="text-lg font-semibold text-blue-900">
                    {recordatoriosEstado.fecha_vencimiento_actual}
                  </p>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-green-700 font-medium mb-1">
                    <User className="w-4 h-4" />
                    Matriculados con email
                  </div>
                  <p className="text-lg font-semibold text-green-900">
                    {recordatoriosEstado.total_matriculados_con_email || 0}
                  </p>
                </div>
              </div>
            )}

            <div className="flex items-center gap-4 pt-2">
              <Button
                onClick={handleEnviarRecordatorios}
                disabled={enviandoRecordatorios}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {enviandoRecordatorios ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Enviar Recordatorio Manual
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  fetchRecordatoriosEstado();
                  fetchRecordatoriosLog();
                }}
                disabled={loadingRecordatorios}
              >
                <RefreshCw className={`w-4 h-4 ${loadingRecordatorios ? 'animate-spin' : ''}`} />
              </Button>
            </div>

            {recordatoriosLog.length > 0 && (
              <div className="mt-4">
                <h4 className="font-medium text-gray-700 mb-2">Últimos envíos</h4>
                <div className="border rounded-lg divide-y max-h-48 overflow-y-auto">
                  {recordatoriosLog.map((log) => (
                    <div key={log.id} className="p-3 hover:bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {log.estadisticas?.fallidos === 0 ? (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          ) : (
                            <XCircle className="w-4 h-4 text-amber-500" />
                          )}
                          <span className="font-medium text-sm">
                            {new Date(log.fecha).toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' })}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            log.tipo === 'automatico' 
                              ? 'bg-purple-100 text-purple-700' 
                              : 'bg-blue-100 text-blue-700'
                          }`}>
                            {log.tipo === 'automatico' ? 'Automático' : 'Manual'}
                          </span>
                        </div>
                        <span className="text-xs text-gray-500">
                          por {log.ejecutado_por}
                        </span>
                      </div>
                      <div className="mt-1 text-sm text-gray-600 flex gap-4">
                        <span className="text-green-600">✓ {log.estadisticas?.enviados || 0} enviados</span>
                        {log.estadisticas?.fallidos > 0 && (
                          <span className="text-red-600">✗ {log.estadisticas.fallidos} fallidos</span>
                        )}
                        <span className="text-gray-400">⏱ {log.duracion_segundos}s</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Warning */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="font-semibold text-amber-800">Advertencia</h3>
          <p className="text-sm text-amber-700">Las acciones de borrado son destructivas. Se crea un backup automático antes de cada operación.</p>
        </div>
      </div>

      {/* Depurar Matriculados - Solo superadmin */}
      {isSuperAdmin && (
        <Card className="border-blue-200">
          <CardHeader>
            <CardTitle className="text-blue-700 flex items-center gap-2">
              <UserX className="w-5 h-5" />
              Depurar Matriculados sin Usuario
            </CardTitle>
            <CardDescription>
              Elimina los matriculados que fueron importados pero nunca crearon un usuario en el sistema.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {!depurarPreview ? (
                <Button variant="outline" onClick={fetchDepurarPreview} disabled={loadingPreview}>
                  {loadingPreview ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Analizando...
                    </>
                  ) : (
                    <>
                      <Database className="w-4 h-4 mr-2" />
                      Ver preview de depuración
                    </>
                  )}
                </Button>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-4 bg-gray-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-gray-900">{depurarPreview.total_matriculados}</p>
                      <p className="text-sm text-gray-500">Total matriculados</p>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-green-600">{depurarPreview.con_usuario}</p>
                      <p className="text-sm text-green-600">Con usuario (se mantienen)</p>
                    </div>
                    <div className="p-4 bg-red-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-red-600">{depurarPreview.sin_usuario}</p>
                      <p className="text-sm text-red-600">Sin usuario (se eliminarán)</p>
                    </div>
                  </div>
                  
                  {depurarPreview.sin_usuario > 0 && (
                    <>
                      <div>
                        <p className="text-sm text-gray-600 mb-2">
                          Para confirmar, escribí <strong>DEPURAR</strong> en el campo:
                        </p>
                        <Input
                          value={depurarText}
                          onChange={(e) => setDepurarText(e.target.value)}
                          placeholder="Escribí DEPURAR para confirmar"
                          className="max-w-xs"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="destructive"
                          disabled={depurarText !== 'DEPURAR' || depurando}
                          onClick={handleDepurarMatriculados}
                        >
                          {depurando ? (
                            <>
                              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                              Depurando...
                            </>
                          ) : (
                            <>
                              <UserX className="w-4 h-4 mr-2" />
                              Eliminar {depurarPreview.sin_usuario} matriculados sin usuario
                            </>
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => {
                            setDepurarPreview(null);
                            setDepurarText('');
                          }}
                        >
                          Cancelar
                        </Button>
                      </div>
                    </>
                  )}
                  
                  {depurarPreview.sin_usuario === 0 && (
                    <p className="text-green-600 text-sm">
                      ✓ No hay matriculados para depurar.
                    </p>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Delete Recursero - Solo superadmin */}
      {isSuperAdmin && (
        <Card className="border-amber-200">
          <CardHeader>
            <CardTitle className="text-amber-700 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Borrar Datos del Recursero
            </CardTitle>
            <CardDescription>
              Esta acción eliminará todos los datos del recursero pero mantendrá los datos personales.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-2">
                  Para confirmar, escribí <strong>BORRAR</strong> en el campo:
                </p>
                <Input
                  value={deleteRecurseroText}
                  onChange={(e) => setDeleteRecurseroText(e.target.value)}
                  placeholder="Escribí BORRAR para confirmar"
                  className="max-w-xs"
                />
              </div>
              <Button
                className="bg-amber-500 hover:bg-amber-600 text-white"
                disabled={deleteRecurseroText !== 'BORRAR' || loading}
                onClick={handleDeleteRecursero}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Borrar Datos del Recursero
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Delete All - Solo superadmin */}
      {isSuperAdmin && (
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="text-red-700 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
            Borrar Base de Datos Completa
          </CardTitle>
          <CardDescription>
            <strong className="text-red-600">PELIGRO:</strong> Esta acción eliminará TODOS los trabajadores sociales y sus datos asociados.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-600 mb-2">
                Para confirmar, escribí <strong>BORRAR TODO</strong> en el campo:
              </p>
              <Input
                value={deleteAllText}
                onChange={(e) => setDeleteAllText(e.target.value)}
                placeholder="Escribí BORRAR TODO para confirmar"
                className="max-w-xs"
              />
            </div>
            <Button
              variant="destructive"
              disabled={deleteAllText !== 'BORRAR TODO' || loading}
              onClick={handleDeleteAll}
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              Borrar Base de Datos Completa
            </Button>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Success Modal */}
      <Dialog open={showSuccess} onOpenChange={setShowSuccess}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-center">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-emerald-600" />
              </div>
              Operación Completada
            </DialogTitle>
          </DialogHeader>
          <p className="text-center text-gray-500">{successMessage}</p>
          <DialogFooter>
            <Button onClick={() => setShowSuccess(false)} className="w-full">
              Aceptar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Restore Confirmation Modal */}
      <Dialog open={showRestoreConfirm} onOpenChange={setShowRestoreConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-center">
              <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                <RefreshCw className="w-8 h-8 text-blue-600" />
              </div>
              Restaurar Backup
            </DialogTitle>
          </DialogHeader>
          {selectedBackup && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-3 rounded-lg text-sm">
                <p><strong>Fecha:</strong> {formatDate(selectedBackup.fecha)}</p>
                <p><strong>Trabajadores:</strong> {selectedBackup.estadisticas?.trabajadores || 0}</p>
                <p><strong>Instituciones:</strong> {selectedBackup.estadisticas?.instituciones || 0}</p>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">
                <strong>⚠️ Advertencia:</strong> Esta acción reemplazará los datos actuales con los del backup.
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-2">
                  Para confirmar, escribí <strong>RESTAURAR</strong>:
                </p>
                <Input
                  value={restoreText}
                  onChange={(e) => setRestoreText(e.target.value)}
                  placeholder="Escribí RESTAURAR para confirmar"
                />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => {
              setShowRestoreConfirm(false);
              setRestoreText('');
              setSelectedBackup(null);
            }}>
              Cancelar
            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              disabled={restoreText !== 'RESTAURAR' || loading}
              onClick={handleRestoreBackup}
            >
              {loading ? 'Restaurando...' : 'Restaurar Backup'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Envío de Recordatorios Result Modal */}
      <Dialog open={showEnvioResult} onOpenChange={setShowEnvioResult}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-center">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
                envioResult?.success ? 'bg-emerald-100' : 'bg-red-100'
              }`}>
                {envioResult?.success ? (
                  <CheckCircle className="w-8 h-8 text-emerald-600" />
                ) : (
                  <XCircle className="w-8 h-8 text-red-600" />
                )}
              </div>
              {envioResult?.success ? 'Envío Completado' : 'Error en el Envío'}
            </DialogTitle>
          </DialogHeader>
          {envioResult && (
            <div className="space-y-3">
              <p className="text-center text-gray-600">{envioResult.mensaje}</p>
              {envioResult.estadisticas && (
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Fecha de vencimiento:</span>
                    <span className="font-medium">{envioResult.fecha_vencimiento}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total matriculados:</span>
                    <span className="font-medium">{envioResult.estadisticas.total_matriculados}</span>
                  </div>
                  <div className="flex justify-between text-green-600">
                    <span>Enviados correctamente:</span>
                    <span className="font-medium">{envioResult.estadisticas.enviados}</span>
                  </div>
                  {envioResult.estadisticas.fallidos > 0 && (
                    <div className="flex justify-between text-red-600">
                      <span>Fallidos:</span>
                      <span className="font-medium">{envioResult.estadisticas.fallidos}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-gray-500">
                    <span>Duración:</span>
                    <span className="font-medium">{envioResult.estadisticas.duracion_segundos}s</span>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setShowEnvioResult(false)} className="w-full">
              Aceptar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==================== MAIN COMPONENT ====================
export default function AdminConfiguracion() {
  const { isSuperAdmin } = useAuth();
  
  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Configuración</h1>

      <Tabs defaultValue="sistema" className="w-full">
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-5 h-auto mb-6">
          <TabsTrigger value="sistema" className="flex items-center gap-2" data-testid="tab-sistema">
            <Settings className="w-4 h-4" />
            Sistema
          </TabsTrigger>
          <TabsTrigger value="anuncios" className="flex items-center gap-2" data-testid="tab-anuncios">
            <Megaphone className="w-4 h-4" />
            Anuncios
          </TabsTrigger>
          <TabsTrigger value="exportar" className="flex items-center gap-2" data-testid="tab-exportar">
            <Download className="w-4 h-4" />
            Exportar
          </TabsTrigger>
          <TabsTrigger value="administradores" className="flex items-center gap-2" data-testid="tab-administradores">
            <ShieldCheck className="w-4 h-4" />
            Administradores
          </TabsTrigger>
          <TabsTrigger value="reset-passwords" className="flex items-center gap-2" data-testid="tab-reset-passwords">
            <KeyRound className="w-4 h-4" />
            Reset Contraseñas
          </TabsTrigger>
        </TabsList>

        <TabsContent value="sistema">
          <SistemaTab isSuperAdmin={isSuperAdmin} />
        </TabsContent>

        <TabsContent value="anuncios">
          <AnunciosAdmin />
        </TabsContent>

        <TabsContent value="exportar">
          <ExportarTab />
        </TabsContent>

        <TabsContent value="administradores">
          <AdministradoresTab />
        </TabsContent>

        <TabsContent value="reset-passwords">
          <ResetPasswordsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
